import { useEffect, useRef } from "react";
import io from "socket.io-client";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  setCredentials,
  setCredentialsLoading,
} from "../features/credentials/credentialsSlice";
import {
  clearHistory,
  clearMessages,
} from "../features/messages/messagesSlice";
import { setChatState } from "../features/settings/settingsSlice";
import { selectToken } from "../features/token/tokenSlice";
import { CredentialsChat } from "../model/credentials-chat";
import { PayloadMessage } from "../model/payload-message";
import {
  clearCredentials,
  retrieveStoredCredentials,
  storeCredentials,
} from "../service/credentials-store.service";

export enum SocketEvents {
  CONNECTED = "connect",
  DISCONNECTED = "disconnect",
  SEND_MESSAGE = "message",
  MESSAGE_RECEIVED = "message",
  MESSAGE_SENT = "message_sent",
  USER_ATTACHED = "user_attached",
  MESSAGE_DELIVERED = "message_delivered",
  MESSAGE_READ = "message_read",
  CLOSE_SESSION = "close_session",
  ERROR = "error",
  CONNECT_ERROR = "connect_error",
  CONNECT_FAILED = "connect_failed",
}

export const useSocketService = () => {
  const tokenChat = useAppSelector(selectToken);
  const HOST =
    !process.env.NODE_ENV || process.env.NODE_ENV === "development"
      ? process.env.REACT_APP_HOST
      : window.location.href.split("/")[2];
  const PROTOCOL = window.location.href.split("/")[0];
  const SOCKET_ADDRESS_FROM_HOST = `${
    PROTOCOL === "http:" ? "ws" : "wss"
  }://${HOST}`;
  const dispatch = useAppDispatch();
  const socketRef = useRef<any>();

  const subscribeConnected = (cb: () => any) => {
    socketRef.current.on(SocketEvents.CONNECTED, cb);
  };

  const subscribeUserAttached = (cb: (credentials: CredentialsChat) => any) => {
    socketRef.current.on(SocketEvents.USER_ATTACHED, cb);
  };

  const subscribeDisconnect = (cb: () => any) => {
    socketRef.current.on(SocketEvents.DISCONNECTED, cb);
  };

  const subscribeConnectError = (cb: () => any) => {
    socketRef.current.on(SocketEvents.CONNECT_ERROR, cb);
  };
  const subscribeConnectFailed = (cb: () => any) => {
    socketRef.current.on(SocketEvents.CONNECT_FAILED, cb);
  };

  const subscribeMessage = (cb: (message: PayloadMessage) => void) => {
    socketRef.current.on(SocketEvents.MESSAGE_RECEIVED, cb);
  };
  const unsubscribeMessage = (cb: (message: PayloadMessage) => void) => {
    socketRef.current.off(SocketEvents.MESSAGE_RECEIVED, cb);
  };
  const subscribeMessageSent = (cb: (message: PayloadMessage) => void) => {
    socketRef.current.on(SocketEvents.MESSAGE_SENT, cb);
  };
  const unsubscribeMessageSent = (cb: (message: PayloadMessage) => void) => {
    socketRef.current.off(SocketEvents.MESSAGE_SENT, cb);
  };
  const subscribeCloseSession = (cb: (message: CredentialsChat) => void) => {
    socketRef.current.on(SocketEvents.CLOSE_SESSION, cb);
  };
  const subscribeError = (cb: (error: { message: string }) => void) => {
    socketRef.current.on(SocketEvents.ERROR, cb);
  };

  const attachUserSocket = (
    credentials: CredentialsChat,
    exclusive: boolean = true
  ) => {
    socketRef.current.emit(SocketEvents.USER_ATTACHED, credentials);
  };

  const emitSocketMessage = (message: PayloadMessage) => {
    // console.log("message emitted", message);
    if (socketRef.current)
      socketRef.current.emit(SocketEvents.SEND_MESSAGE, message);
  };

  const emitDeliveredEvent = (message: PayloadMessage) => {
    // console.log("%c message delivered", "color:yellow");
    if (socketRef.current)
      socketRef.current.emit(SocketEvents.MESSAGE_DELIVERED, message);
  };

  const emitReadEvent = (message: PayloadMessage) => {
    if (socketRef.current)
      socketRef.current.emit(SocketEvents.MESSAGE_READ, message);
  };

  const emitCloseSession = (message: CredentialsChat) => {
    if (socketRef.current)
      socketRef.current.emit(SocketEvents.CLOSE_SESSION, message);
  };

  const onDisconnect = () => {
    if (socketRef.current) {
      // console.log("DISCONNECT");
      socketRef.current.disconnect();
    }
  };

  const onConnect = () => {
    socketRef.current = io(SOCKET_ADDRESS_FROM_HOST, {
      path: `/${tokenChat}/socket.io`,
      transports: ["websocket", "polling"],
    });

    // Log('on connect', socketRef.current, '', 'yellow');

    dispatch(setCredentialsLoading(true));
    subscribeConnectError(() => {
      dispatch(setCredentialsLoading(false));
    });
    subscribeConnectFailed(() => {
      dispatch(setCredentialsLoading(false));
    });
    subscribeError(() => {
      dispatch(setCredentials(null));
      onDisconnect();
      dispatch(clearHistory());
      dispatch(clearMessages());
      clearCredentials();
      onConnect();
    });
    subscribeCloseSession(() => {
      dispatch(setChatState(false));
    });
    subscribeUserAttached((newCredentials: any) => {
      dispatch(setCredentials(newCredentials));
      storeCredentials(newCredentials);
      subscribeConnected(() => {
        newCredentials && attachUserSocket(newCredentials);
      });
      dispatch(setCredentialsLoading(false));
    });

    const storedCredentials = retrieveStoredCredentials();
    if (storedCredentials) {
      attachUserSocket(storedCredentials);
    } else {
      dispatch(setCredentialsLoading(false));
    }
  };

  useEffect(() => {
    onConnect();

    return () => {
      // onDisconnect();
      //   disconnectApp();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    current: socketRef.current,
    attachUserSocket,
    onDisconnect,
    emitDeliveredEvent,
    subscribeDisconnect,
    subscribeMessage,
    unsubscribeMessage,
    unsubscribeMessageSent,
    subscribeMessageSent,
    subscribeCloseSession,
    onConnect,
    subscribeError,
    emitSocketMessage,
    emitCloseSession,
    emitReadEvent,
  };
};
